/*
 * Developed for EPSON AMERICA INC. by Softeq Development Corporation
 * http://www.softeq.com
 */

import React from 'react'
import { Field } from 'redux-form'
import { MAX_LENGTHS, CARD_FIELD_NAMES } from 'constants/forms'
import { CARD_TYPES } from 'constants/payment'
import { Text, GetTextPath } from 'hocs/localization'
import { Select, Input, Checkbox, Captcha } from 'apps/FormFields'
import Button from 'elements/Button'
import {
  createValidation,
  createValidator,
  hasRequired,
  hasRequiredCheckbox,
  hasCardNumber,
  hasCardCVV,
  hasSelectedValue,
  onlyDigits,
  hasFullName,
  hasNameOnCard,
} from 'utils/form'
import * as styles from './AddCardForm.scss'

interface IState {
  isDisabled
}

interface IProps {
  isDisabled?: boolean,
  t: Text,
  getTextPath: GetTextPath,
  stateOptions: any[],
  submitting: boolean,
  valid: boolean,
  router: any,
  expirationMonthOptions: any[],
  expirationYearOptions: any[],
  onConfirmCorrectedAddress: () => void,
  cardType: CARD_TYPES,
  onChangeCardNumber: (event: Event, value: string) => void,
  backUrl?: string,
  isShowBackLink: boolean,
  isEdit: boolean,
  onSubmit: () => Promise<any>,
  isCardNumberAutoFocus: boolean,
  handleSubmit: () => void,
  isSubmitting: boolean,
  isUserUnableToUpdateInfo: boolean,
  cardInfo: any,
  isCaptchaVerified: boolean,
  onVerifyCaptcha: (response: string) => void,
  captchaRef: (ref: React.ReactElement<any>) => React.Ref<any>,
  isCaptchaEnabled: boolean,
  isCaptchaWhiteListed: boolean,
  captchaFieldName: string,
}

class AddCardForm extends React.Component<IProps, IState> {
  public readonly state = {
    isDisabled: false,
  }

  public requiredFieldValidation = createValidation([
    createValidator(hasRequired, this.props.getTextPath('errors.textField')),
  ])

  public requiredCheckboxValidation = createValidation([
    createValidator(hasRequiredCheckbox, ''),
  ])

  private ccNumberValidation = createValidation([
    createValidator(
      value => hasCardNumber(value, this.props.cardType),
      () => this.props.getTextPath('errors.cardNumber'),
    ),
  ])

  private cardCVVValidation = createValidation([
    createValidator(
      value => hasCardCVV(value, this.props.cardType),
      () => this.props.getTextPath('errors.cardCVV'),
    ),
  ])
  public selectFieldValidation = createValidation([
    createValidator(hasSelectedValue, this.props.getTextPath('errors.select')),
  ])

  private cardFullNameValidation = createValidation([
    createValidator(value => hasFullName(value), this.props.getTextPath('errors.cardHolderFullName')),
  ])

  private cardNameValidation = createValidation([
    createValidator(value => hasNameOnCard(value), this.props.getTextPath('errors.cardHolderName')),
  ])

  private onPasteCardNumber = (event) => {
    const value = event.clipboardData.getData('text/plain')
    this.props.onChangeCardNumber(event, value)
  }

  public componentWillReceiveProps (nextProps) {
    if (this.props.isDisabled !== nextProps.isDisabled) {
      this.setState({
        isDisabled: nextProps.isDisabled,
      })
    }
  }

  public render () {
    const {
      t,
      handleSubmit,
      expirationMonthOptions,
      expirationYearOptions,
      cardType,
      onChangeCardNumber,
      isEdit,
      submitting,
      valid,
      isCardNumberAutoFocus,
      isSubmitting,
      isUserUnableToUpdateInfo,
      cardInfo,
      isCaptchaEnabled,
      isCaptchaWhiteListed,
      captchaFieldName,
      onVerifyCaptcha,
      captchaRef,
    } = this.props

    const isFieldDisabled = (isEdit && isUserUnableToUpdateInfo) || this.state.isDisabled || isSubmitting

    return (
      <form className={styles.root}>
        {isEdit && (
          <div className={styles.cardInfo}>
            <div>{t('cardOnFile')}:</div>
            <div className={styles.cardData}>
              <div>*{cardInfo.last4Digits}</div>
              <div>{cardInfo.expirationMonth}/{cardInfo.expirationYear}</div>
            </div>
          </div>
        )}
        <div className={styles.fieldsContainer}>
          <div className={styles.fieldWrap}>
            <Field
              disabled={isFieldDisabled}
              type='text'
              name={CARD_FIELD_NAMES.paymentCreditCard}
              component={Input}
              placeholder={t('cardNumberPlaceholder')}
              validate={[this.requiredFieldValidation, this.ccNumberValidation]}
              mask={cardType === CARD_TYPES.amex ? t('cardNumberShortMask') : t('cardNumberMask')}
              maskChar={null}
              onChange={onChangeCardNumber}
              onPaste={this.onPasteCardNumber}
              label={t('payment.ccNumberLabel')}
              autoFocus={isCardNumberAutoFocus}
              required
            />
          </div>
          <div className={styles.fieldWrap}>
            <Field
              disabled={isFieldDisabled}
              type='text'
              name={CARD_FIELD_NAMES.nameOnCard}
              component={Input}
              maxLength={MAX_LENGTHS.name}
              validate={[this.requiredFieldValidation, this.cardFullNameValidation, this.cardNameValidation]}
              label={t('payment.nameOnCard')}
              required
            />
          </div>
          <div className={styles.fieldWrap}>
            <Field
              disabled={isFieldDisabled}
              type='select'
              name={CARD_FIELD_NAMES.expMonth}
              component={Select}
              options={expirationMonthOptions}
              placeholder={t('payment.expirationMonthPlaceholder')}
              validate={this.selectFieldValidation}
              id='expMonth'
              label={t('payment.expirationMonthLabel')}
              required
            />
          </div>
          <div className={styles.fieldWrap}>
            <Field
              disabled={isFieldDisabled}
              type='select'
              name={CARD_FIELD_NAMES.expYear}
              component={Select}
              options={expirationYearOptions}
              placeholder={t('payment.expirationYearPlaceholder')}
              validate={this.selectFieldValidation}
              id='expYear'
              label={t('payment.expirationYearLabel')}
              required
            />
          </div>
          <div className={styles.fieldWrap}>
            <Field
              disabled={isFieldDisabled}
              type='cvv'
              name={CARD_FIELD_NAMES.CVV}
              component={Input}
              validate={[this.requiredFieldValidation, this.cardCVVValidation]}
              placeholder={cardType === CARD_TYPES.amex
                ? t('cardCVVLongPlaceholder')
                : t('cardCVVPlaceholder')
              }
              maxLength={cardType === CARD_TYPES.amex ? MAX_LENGTHS.cardCVVLong :  MAX_LENGTHS.cardCVV}
              normalize={onlyDigits}
              label={t('payment.cardCVVLabel')}
              required
            />
          </div>
          <div className={styles.agreementCheckbox}>
                  <Field
                    disabled={submitting}
                    type='text'
                    name={CARD_FIELD_NAMES.agreementCheckbox}
                    component={Checkbox}                    
                    label={t('payment.cardAgreement')}
                    validate={this.requiredCheckboxValidation}                    
                  />
          </div>
          {(isCaptchaEnabled && !isCaptchaWhiteListed) && (
            <div className={styles.fieldWrapCaptcha}>
              <Field
                id='captcha'
                disabled={isFieldDisabled}
                captchaRef={captchaRef}
                name={captchaFieldName}
                component={Captcha}
                verifyCallback={onVerifyCaptcha}
              />
            </div>)}
          <div className={styles.verbiageOuter}>
            <div className={styles.verbiageInner}>{t('payment.ccVerbiage1')}</div>
            <div className={styles.verbiageInner}>{t('payment.ccVerbiage2')}</div>
          </div>
        </div>
        {(isEdit && !isUserUnableToUpdateInfo) && (
          <div className={styles.submitButton}>
            <Button fluid onClick={handleSubmit} disabled={submitting || !valid}>
              {submitting ? t('saving') : t('saveChanges')}
            </Button>
          </div>
        )}
      </form>
    )
  }
}

export default AddCardForm
